import { useEffect, useMemo, useState, type RefObject } from 'react';
import { useMonaco, type Monaco } from '@monaco-editor/react';
import {
  hasOverlappingSyntaxError,
  type LspSyntaxMarker,
} from '../../visual-query-editor/compact/lspParseErrorSignal';
import { useLocalSyntaxErrorEntries } from '../../visual-query-editor/compact/useLocalSyntaxErrorEntries';
import { localSyntaxErrorFires } from '../../visual-query-editor/compact/localSyntaxErrorSignal';
import type { DatabaseEngineId } from '../../../../core/engines';
import type { CursorStatement, RawCursorSegment } from './useCursorStatementTracking';

// --- LSP parse-error signal wiring (VQE Polish, EDG-11..15) ---------------
// `SqlMonacoHandle` (the tab's `editorRef`) doesn't expose the raw Monaco
// `ICodeEditor`/model/URI — only imperative methods like `getValue`/
// `setMarkers`. Workspace keeps EVERY open query tab's `SqlMonaco` mounted
// simultaneously (hidden via CSS `hidden`, not unmounted — see
// `Workspace.tsx`), so `monaco.editor.getEditors()`/`getModelMarkers()` are
// page-wide: this helper finds the ONE editor that lives inside THIS tab's
// own container (`sqlEditorContainerRef`) via plain DOM containment, so a
// broken statement in another open tab can never leak in as a
// false-positive overlap.
function findOwnEditor(monacoInstance: Monaco, container: HTMLElement | null) {
  if (!container) return undefined;
  return monacoInstance.editor.getEditors().find((candidate) => {
    const node = candidate.getDomNode?.();
    return !!node && container.contains(node);
  });
}

/** Projects a real Monaco marker's numeric `MarkerSeverity` onto the plain
 * string-union `lspParseErrorSignal.ts` expects, so that module stays
 * Monaco-free and unit-testable without a live editor. */
function toLspSeverity(severity: number, monacoInstance: Monaco): LspSyntaxMarker['severity'] {
  if (severity === monacoInstance.MarkerSeverity.Error) return 'error';
  if (severity === monacoInstance.MarkerSeverity.Warning) return 'warning';
  if (severity === monacoInstance.MarkerSeverity.Info) return 'info';
  return 'hint';
}

type UseSyntaxErrorSignalParams = {
  engineId: DatabaseEngineId;
  /** DOM anchor scoped to this tab's Monaco mount (NOT the results pane). */
  sqlEditorContainerRef: RefObject<HTMLDivElement | null>;
  currentCursorStatement: CursorStatement | null;
  rawCursorSegment: RawCursorSegment | null;
};

/**
 * Never-silent parse-error signal (VQE Polish, EDG-11..15). Two independent
 * sources compose into one `hasSyntaxError` verdict for the cursor statement:
 *
 *   (A) an Error marker under owner `'sql'` (PostgreSQL LSP / mock) OR
 *       `'sql-syntax'` (in-house MSSQL ANTLR∧tree-sitter pass) — the language
 *       service's own signal, which ALSO draws its own squiggle; and
 *   (B) the LOCAL per-dialect tree-sitter recovery predicate
 *       (`useLocalSyntaxErrorEntries` + `localSyntaxErrorFires`) — the signal
 *       for the four engines that publish NO syntax marker (mysql / oracle /
 *       sqlite, and mssql / pgsql when their pass is silent). It is
 *       deliberately conservative (tree-sitter recovery overlap AND an
 *       unambiguous structural break) so it NEVER fires on valid SQL.
 *
 * Both are computed against the RAW cursor segment (`rawCursorSegment`), not
 * `currentCursorStatement`: the finding shapes (`… WHERE (Color =`) are not
 * pencil-reachable, so `currentCursorStatement` is null for them — the raw
 * segment is what lets the chip + squiggle attach to a genuinely broken
 * statement. Behavior carried verbatim from the previous inline implementation.
 */
export function useSyntaxErrorSignal({
  engineId,
  sqlEditorContainerRef,
  currentCursorStatement,
  rawCursorSegment,
}: UseSyntaxErrorSignalParams) {
  const monacoInstance = useMonaco();
  const [sqlSyntaxMarkers, setSqlSyntaxMarkers] = useState<LspSyntaxMarker[]>([]);
  useEffect(() => {
    if (!monacoInstance) return;
    const readMarkers = () => {
      const ownEditor = findOwnEditor(monacoInstance, sqlEditorContainerRef.current);
      const model = ownEditor?.getModel?.() ?? null;
      if (!model) {
        setSqlSyntaxMarkers([]);
        return;
      }
      // Read BOTH syntax-error owners: `'sql'` (PostgreSQL LSP / mock) and
      // `'sql-syntax'` (MSSQL). The prior task read only `'sql'`, which is why
      // the chip was inert for mssql (whose squiggle owner is `'sql-syntax'`).
      const rawMarkers = [
        ...monacoInstance.editor.getModelMarkers({ owner: 'sql', resource: model.uri }),
        ...monacoInstance.editor.getModelMarkers({ owner: 'sql-syntax', resource: model.uri }),
      ];
      setSqlSyntaxMarkers(
        rawMarkers.map((marker) => ({
          severity: toLspSeverity(marker.severity, monacoInstance),
          startLineNumber: marker.startLineNumber,
          startColumn: marker.startColumn,
          endLineNumber: marker.endLineNumber,
          endColumn: marker.endColumn,
        })),
      );
    };
    // Markers arrive async (LSP round-trip); fetch once now (covers the case
    // where they were already set before this effect subscribed) and again
    // every time ANY model's markers change page-wide — a transient
    // no-marker window right after a keystroke is fine, it converges on the
    // next `onDidChangeMarkers` firing.
    readMarkers();
    const disposable = monacoInstance.editor.onDidChangeMarkers(readMarkers);
    return () => disposable.dispose();
  }, [monacoInstance, sqlEditorContainerRef]);

  // Source (B): debounced local tree-sitter recovery spans for this buffer.
  const localSyntaxErrorEntries = useLocalSyntaxErrorEntries(
    monacoInstance,
    sqlEditorContainerRef,
    engineId,
  );

  // Marker overlap (A) and conservative local predicate (B) for the raw cursor
  // segment. `hasLocalSyntaxError` can never be true for a valid statement:
  // `localSyntaxErrorFires` requires an unambiguous structural break, which a
  // complete, balanced statement never has (BUG-4 preserved).
  const hasMarkerSyntaxError = useMemo(
    () => hasOverlappingSyntaxError(sqlSyntaxMarkers, rawCursorSegment?.range ?? null),
    [sqlSyntaxMarkers, rawCursorSegment?.range],
  );
  const hasLocalSyntaxError = useMemo(
    () =>
      rawCursorSegment
        ? localSyntaxErrorFires(
            localSyntaxErrorEntries,
            rawCursorSegment.startOffset,
            rawCursorSegment.endOffset,
            rawCursorSegment.text,
          )
        : false,
    [localSyntaxErrorEntries, rawCursorSegment],
  );
  const hasSyntaxError = hasMarkerSyntaxError || hasLocalSyntaxError;

  // A confirmed-broken statement the pencil path gave up on (non-pencil-reachable
  // AND flagged by a real syntax-error signal). This is what lets the chip +
  // squiggle attach to the EDG-11..15 finding shapes. Never non-null for a
  // pencil-reachable statement (those flow through `currentCursorStatement`), so
  // the pencil is never wrongly suppressed.
  const brokenCursorStatement =
    currentCursorStatement === null && rawCursorSegment && hasSyntaxError
      ? rawCursorSegment
      : null;
  // The statement the compact affordance renders for: the pencil-reachable one,
  // else the confirmed-broken one. Mutually exclusive by construction.
  const affordanceStatement = currentCursorStatement ?? brokenCursorStatement;

  // NOTE: this hook deliberately does NOT dismiss the suggest widget when the
  // cursor's statement gains a syntax error. An earlier EDG-11/13 residual
  // triggered `hideSuggestWidget` on that transition to keep an auto-opened
  // snippet popup off a squiggled statement, but a mid-typing statement is
  // invalid precisely when completions matter most: at `SELECT * FROM users, `
  // the debounced parse flags the trailing comma ~300ms after the widget opens
  // and killed the table list — including one the user had just asked for with
  // Ctrl+Space (guarded by
  // `features/sqlite/sqlite-intellisense-after-table-ref.spec.ts`). Squiggle +
  // explain-chip already communicate the error; suppress-at-open would be the
  // place to fight popup occlusion, never kill-after-open.

  return {
    hasMarkerSyntaxError,
    hasLocalSyntaxError,
    affordanceStatement,
  };
}
